import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';
import { supabase } from './supabase';

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/webauthn`;

async function callWebAuthn(action: string, payload: Record<string, unknown> = {}, token?: string) {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({ action, ...payload }),
  });

  let data: any;
  try {
    data = await res.json();
  } catch {
    throw new Error(`Server returned ${res.status}: ${res.statusText}`);
  }
  if (!res.ok) {
    throw new Error(data.error || `Server error (${res.status})`);
  }
  return data;
}

export function isWebAuthnSupported(): boolean {
  return browserSupportsWebAuthn();
}

export async function registerBiometric(deviceName?: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const { options } = await callWebAuthn('generate-registration-options', {}, session.access_token);

  const attestationResponse = await startRegistration({ optionsJSON: options });

  await callWebAuthn(
    'verify-registration',
    { attestationResponse, deviceName: deviceName || 'This Device' },
    session.access_token
  );
}

export async function authenticateWithBiometric(
  email?: string
): Promise<{ access_token: string; refresh_token: string }> {
  const { options, userId } = await callWebAuthn('generate-authentication-options', email ? { email } : {});

  const assertionResponse = await startAuthentication({ optionsJSON: options });

  const { session } = await callWebAuthn('verify-authentication', {
    assertionResponse,
    userId,
  });

  return session;
}

export async function getUserCredentials() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('webauthn_credentials')
    .select('id, credential_id, device_name, created_at')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function removeCredential(id: string): Promise<void> {
  const { error } = await supabase
    .from('webauthn_credentials')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export function getWebAuthnErrorMessage(err: unknown): string {
  if (!(err instanceof Error)) return 'An unknown error occurred.';
  console.error('WebAuthn error:', err);
  switch (err.name) {
    case 'NotAllowedError':
      return 'User canceled or biometrics not available on this device.';
    case 'SecurityError':
      return 'Page must be served over HTTPS for biometric authentication.';
    case 'InvalidStateError':
      return 'This credential already exists or the request options are invalid.';
    case 'NotSupportedError':
      return 'This browser or device does not support the required authentication method.';
    case 'AbortError':
      return 'The operation was aborted. Please try again.';
    default:
      return err.message || 'Biometric authentication failed.';
  }
}

export async function hasCredentialForEmail(email: string): Promise<boolean> {
  try {
    await callWebAuthn('generate-authentication-options', { email });
    return true;
  } catch {
    return false;
  }
}
